import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
    Sparkles, Send, Plus, Trash2, MessageSquare, Loader2, ShieldCheck,
    Stethoscope, Pill, FileText, X,
} from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import PrescriptionPickerSheet from "../components/PrescriptionPickerSheet";
import { api } from "../api/client";

const SUGGESTIONS = [
    { icon: <Stethoscope className="h-4 w-4" />, text: "I have a headache, what can I take?" },
    { icon: <Pill className="h-4 w-4" />, text: "How does Paracetamol work?" },
    { icon: <FileText className="h-4 w-4" />, text: "Help me understand my latest prescription" },
];

const AssistantBubble = ({ content }) => (
    <div className="flex items-start gap-3" data-testid="msg-assistant">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-gradient text-white">
            <Sparkles className="h-4 w-4" />
        </div>
        <div className="glass-card max-w-[88%] rounded-2xl rounded-tl-md px-4 py-3 text-sm leading-relaxed text-zinc-800 shadow-soft">
            <div className="prose prose-sm prose-zinc max-w-none prose-p:my-2 prose-strong:font-semibold prose-strong:text-zinc-900 prose-ul:my-2 prose-li:my-0">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ""}</ReactMarkdown>
            </div>
        </div>
    </div>
);

const UserBubble = ({ content, prescriptionId }) => (
    <div className="flex items-start justify-end gap-3" data-testid="msg-user">
        <div className="max-w-[80%] rounded-2xl rounded-tr-md bg-brand-50 px-4 py-3 text-sm leading-relaxed text-zinc-900 border border-brand-100">
            {prescriptionId && (
                <p className="mb-1 inline-flex items-center gap-1 text-[11px] font-medium text-brand-700">
                    <ShieldCheck className="h-3 w-3" /> Prescription attached
                </p>
            )}
            <p className="whitespace-pre-wrap">{content}</p>
        </div>
    </div>
);

const TypingBubble = () => (
    <div className="flex items-start gap-3" data-testid="msg-typing">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-gradient text-white">
            <Sparkles className="h-4 w-4" />
        </div>
        <div className="glass-card rounded-2xl rounded-tl-md px-4 py-3 shadow-soft">
            <div className="flex items-center gap-1.5">
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-500" style={{ animationDelay: "0ms" }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-500" style={{ animationDelay: "120ms" }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-500" style={{ animationDelay: "240ms" }} />
            </div>
        </div>
    </div>
);

const ChatContent = () => {
    const { id: routeId } = useParams();
    const navigate = useNavigate();
    const [conversations, setConversations] = useState([]);
    const [activeId, setActiveId] = useState(routeId || null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [loadingConvs, setLoadingConvs] = useState(true);
    const [loadingMsgs, setLoadingMsgs] = useState(false);
    const [rxOpen, setRxOpen] = useState(false);
    const [attachedRx, setAttachedRx] = useState(null);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    const activeConv = useMemo(() => conversations.find((c) => c.id === activeId) || null, [conversations, activeId]);

    const loadConvs = async () => {
        try {
            setLoadingConvs(true);
            const { data } = await api.get("/chat/conversations");
            setConversations(data);
            return data;
        } finally {
            setLoadingConvs(false);
        }
    };

    const loadMessages = async (convId) => {
        if (!convId) return;
        try {
            setLoadingMsgs(true);
            const { data } = await api.get(`/chat/conversations/${convId}`);
            setMessages(data.messages || []);
        } catch {
            setMessages([]);
        } finally {
            setLoadingMsgs(false);
        }
    };

    useEffect(() => {
        (async () => {
            const list = await loadConvs();
            if (!activeId && list.length > 0) setActiveId(list[0].id);
        })();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (activeId) loadMessages(activeId);
    }, [activeId]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages, sending]);

    const newChat = async () => {
        try {
            const { data } = await api.post("/chat/conversations", {});
            await loadConvs();
            setActiveId(data.id);
            setMessages([]);
            setAttachedRx(null);
            setTimeout(() => inputRef.current?.focus(), 50);
        } catch {
            toast.error("Could not start a new chat");
        }
    };

    const archiveChat = async (convId, e) => {
        e?.stopPropagation();
        if (!window.confirm("Delete this conversation?")) return;
        try {
            await api.delete(`/chat/conversations/${convId}`);
            const updated = conversations.filter((c) => c.id !== convId);
            setConversations(updated);
            if (activeId === convId) {
                setActiveId(updated[0]?.id || null);
                setMessages([]);
            }
        } catch {
            toast.error("Delete failed");
        }
    };

    const sendMessage = async (overrideText) => {
        const text = (overrideText ?? draft).trim();
        if (!text || sending) return;

        let convId = activeId;
        if (!convId) {
            try {
                const { data } = await api.post("/chat/conversations", {});
                convId = data.id;
                setActiveId(convId);
            } catch {
                toast.error("Could not start chat");
                return;
            }
        }

        const tempId = `tmp-${Date.now()}`;
        const optimisticUser = {
            id: tempId,
            conversation_id: convId,
            role: "user",
            content: text,
            created_at: new Date().toISOString(),
            metadata: attachedRx ? { attached_prescription_id: attachedRx.id } : {},
        };
        setMessages((m) => [...m, optimisticUser]);
        setDraft("");
        setSending(true);
        const attachedRxIdSnapshot = attachedRx?.id || null;
        setAttachedRx(null);

        try {
            const { data: reply } = await api.post(`/chat/conversations/${convId}/messages`, {
                content: text,
                ...(attachedRxIdSnapshot ? { attached_prescription_id: attachedRxIdSnapshot } : {}),
            });
            setMessages((m) => [...m.filter((x) => x.id !== tempId), optimisticUser, reply]);
            // refresh conversation list so titles/order update
            loadConvs();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.code : null;
            const msg = typeof detail === "string" ? detail : detail?.message || "Could not send message";
            if (code === "RATE_LIMITED") toast.error(msg);
            else toast.error(msg);
            // remove the optimistic user message on hard failure (server didn't accept)
            setMessages((m) => m.filter((x) => x.id !== tempId));
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const isEmpty = !loadingMsgs && messages.length === 0;

    return (
        <div className="grid h-[calc(100vh-180px)] gap-6 lg:grid-cols-[300px_1fr]" data-testid="chat-page">
            {/* Sidebar */}
            <aside className="flex h-full flex-col overflow-hidden rounded-3xl border border-zinc-100 bg-white p-3 shadow-soft" data-testid="chat-sidebar">
                <button onClick={newChat} className="btn-brand !py-2.5 !text-sm" data-testid="chat-new-btn">
                    <Plus className="h-4 w-4" /> New chat
                </button>
                <div className="mt-4 flex-1 overflow-y-auto">
                    {loadingConvs ? (
                        <div className="space-y-2 p-1">
                            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-14 skeleton rounded-xl" />)}
                        </div>
                    ) : conversations.length === 0 ? (
                        <div className="px-2 py-8 text-center text-xs text-zinc-500">No conversations yet</div>
                    ) : (
                        <ul className="space-y-1">
                            {conversations.map((c) => (
                                <li key={c.id}>
                                    <button
                                        onClick={() => setActiveId(c.id)}
                                        className={`group flex w-full items-start gap-2 rounded-xl p-3 text-left transition-colors ${activeId === c.id ? "bg-brand-50 text-brand-900" : "hover:bg-zinc-50"}`}
                                        data-testid={`conv-${c.id}`}
                                    >
                                        <MessageSquare className={`mt-0.5 h-4 w-4 shrink-0 ${activeId === c.id ? "text-brand-700" : "text-zinc-400"}`} />
                                        <div className="min-w-0 flex-1">
                                            <p className="line-clamp-1 text-sm font-medium">{c.title}</p>
                                            <p className="line-clamp-1 text-xs text-zinc-500">{c.last_message_preview || "Start a conversation..."}</p>
                                        </div>
                                        <span
                                            onClick={(e) => archiveChat(c.id, e)}
                                            role="button"
                                            tabIndex={0}
                                            className="rounded-full p-1 text-zinc-400 opacity-0 transition group-hover:opacity-100 hover:bg-zinc-100 hover:text-red-500"
                                            aria-label="Delete conversation"
                                            data-testid={`conv-delete-${c.id}`}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </aside>

            {/* Main chat */}
            <section className="flex h-full flex-col overflow-hidden rounded-3xl border border-zinc-100 bg-white shadow-soft" data-testid="chat-main">
                <header className="flex items-center justify-between gap-3 border-b border-zinc-100 px-6 py-4">
                    <div className="flex items-center gap-2">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-brand-gradient text-white">
                            <Sparkles className="h-4 w-4" />
                        </span>
                        <div>
                            <p className="text-sm font-semibold text-zinc-900">PILLS Assistant</p>
                            <p className="text-[11px] text-zinc-500">Powered by Gemini 2.5 Flash</p>
                        </div>
                    </div>
                    <div className="hidden rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-medium text-amber-800 sm:inline-flex">
                        General info only — not medical advice
                    </div>
                </header>

                <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5">
                    {isEmpty ? (
                        <div className="mx-auto flex h-full max-w-xl flex-col items-center justify-center text-center" data-testid="chat-empty">
                            <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-3xl bg-brand-gradient text-white">
                                <Sparkles className="h-6 w-6" />
                            </div>
                            <h2 className="text-2xl font-semibold tracking-tight">Ask me anything about your health, medicines, or prescriptions</h2>
                            <p className="mt-2 text-sm text-zinc-500">I can explain how medicines work, suggest OTC options for minor issues, and help you make sense of prescriptions. I'm not a doctor.</p>
                            <div className="mt-8 grid w-full gap-2">
                                {SUGGESTIONS.map((s) => (
                                    <button
                                        key={s.text}
                                        onClick={() => sendMessage(s.text)}
                                        className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-left text-sm transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-brand"
                                        data-testid={`suggestion-${s.text.slice(0, 14).toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                        <span className="text-brand-700">{s.icon}</span>
                                        <span>{s.text}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : loadingMsgs ? (
                        <div className="space-y-4">
                            <div className="h-16 w-3/4 skeleton rounded-2xl" />
                            <div className="ml-auto h-12 w-1/2 skeleton rounded-2xl" />
                            <div className="h-24 w-2/3 skeleton rounded-2xl" />
                        </div>
                    ) : (
                        <ul className="space-y-5">
                            {messages.map((m) => (
                                <li key={m.id}>
                                    {m.role === "user"
                                        ? <UserBubble content={m.content} prescriptionId={m.metadata?.attached_prescription_id} />
                                        : <AssistantBubble content={m.content} />}
                                </li>
                            ))}
                            {sending && <li><TypingBubble /></li>}
                        </ul>
                    )}
                </div>

                {/* Input */}
                <div className="border-t border-zinc-100 px-6 py-4">
                    {attachedRx && (
                        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700" data-testid="chat-attached-rx">
                            <ShieldCheck className="h-3 w-3" /> Rx attached: {attachedRx.doctor_name || "Prescription"}
                            <button onClick={() => setAttachedRx(null)} className="rounded-full p-0.5 hover:bg-brand-100" aria-label="Remove attachment">
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    )}
                    <div className="flex items-end gap-2">
                        <button
                            onClick={() => setRxOpen(true)}
                            disabled={sending}
                            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-600 hover:border-brand-300 hover:text-brand-700 disabled:opacity-50"
                            title="Attach prescription"
                            data-testid="chat-attach-rx-btn"
                        >
                            <FileText className="h-4 w-4" />
                        </button>
                        <textarea
                            ref={inputRef}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder="Ask a health or medicine question..."
                            rows={1}
                            maxLength={4000}
                            className="input-clean min-h-[44px] resize-none py-3"
                            data-testid="chat-input"
                        />
                        <button
                            onClick={() => sendMessage()}
                            disabled={sending || !draft.trim()}
                            className="btn-brand !h-11 !w-11 !rounded-full !p-0"
                            aria-label="Send"
                            data-testid="chat-send-btn"
                        >
                            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </button>
                    </div>
                    <p className="mt-2 text-center text-[11px] text-zinc-400">
                        Press Enter to send • Shift+Enter for newline
                    </p>
                </div>
            </section>

            <PrescriptionPickerSheet
                open={rxOpen}
                onClose={() => setRxOpen(false)}
                onSelect={(rx) => { setAttachedRx(rx); setRxOpen(false); }}
            />
        </div>
    );
};

const Chat = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-8 lg:px-12">
            <header className="mb-6">
                <p className="label-uppercase">Health assistant</p>
                <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 sm:text-4xl">Ask the AI</h1>
            </header>
            <AuthGuard>
                <ChatContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Chat;
